// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { QuickTableEditingInfo } from '../elements/QuickTable/QuickTableMisc';
import { BaseItem, ColumnData } from './BaseItem';
import { ColumnParams } from './ColumnParams';
import { StringUtils } from '@shared/utils/StringUtils';
import { DynProperty } from '@shared/commons/DynProperty';
import { StatementFormatter } from '@shared/commons/cache/Formatters/StatementFormatter';
import { TableTotalsVisibility } from './TableTotalsVisibility';
import { QTAlignment } from '../elements/QuickTable/QuickTableColumn';
import { ThemeManager } from '../misc/ThemeManager';
export class StatementItem extends BaseItem {
    constructor(statement, sessionSettings) {
        super(sessionSettings);
        this.statement = statement;
        if (!isNullOrUndefined(statement)) {
            this.QuickTableEditingInfoMap[StatementItem.DETAILS_COL_INDEX] = new QuickTableEditingInfo(DynProperty.TRUNCATE_TEXT_ADDING_ELLIPSIS);
            this.QuickTableEditingInfoMap[StatementItem.DETAILS_COL_INDEX].TooltipKey = statement.Details;
        }
    }
    getQuickTableCellForeground(column) {
        const colors = ThemeManager.colors;
        switch (column) {
            case StatementItem.INSTRUMENT_COL_INDEX:
                return colors.tableSymbolForeground;
            case StatementItem.QUANTITY_COL_INDEX:
                return colors.tableQuantityForeground;
            default:
                return super.getQuickTableCellForeground(column);
        }
    }
    ColumnCount() {
        return StatementItem.columnsParams.length;
    }
    getColumnData(column) {
        let value = null;
        let formattedValue = null;
        if (this.statement === null) {
            value = null;
            formattedValue = '';
        }
        value = this.getColumnValue(column);
        switch (column) {
            case StatementItem.DATE_COL_INDEX:
                formattedValue = StatementFormatter.DateStr(this.statement);
                break;
            case StatementItem.TRADE_OPENING_DATE_COL_INDEX:
                formattedValue = StatementFormatter.TradeOpeningDateStr(this.statement);
                break;
            case StatementItem.AMOUNT_COL_INDEX:
                formattedValue = StatementFormatter.AmountStr(this.statement);
                break;
            case StatementItem.OPERATION_TYPE_COL_INDEX:
                formattedValue = StatementFormatter.OperationTypeStr(this.statement);
                break;
            default:
                formattedValue = value != null ? value.toString() : '';
                break;
        }
        if (formattedValue === StringUtils.NOT_AVAILABLE_OLD_CONSTANT) {
            formattedValue = StringUtils.NOT_AVAILABLE;
        }
        return new ColumnData(value, formattedValue, this.GetGroupByValue(column, value, formattedValue));
    }
    getColumnValue(column) {
        const statement = this.statement;
        if (statement === null) {
            return null;
        }
        switch (column) {
            case StatementItem.ACCOUNT_COL_INDEX:
                return statement.Account;
            case StatementItem.INSTRUMENT_COL_INDEX:
                return statement.InstrumentStr;
            case StatementItem.DATE_COL_INDEX:
                return statement.Date;
            case StatementItem.OPERATION_TYPE_COL_INDEX:
                return statement.OperationTypeId;
            case StatementItem.OPERATION_ID_COL_INDEX:
                return statement.OperationID;
            case StatementItem.AMOUNT_COL_INDEX:
                return statement.Amount;
            case StatementItem.OPEN_PRICE_COL_INDEX:
                return statement.OpenPrice;
            case StatementItem.CLOSE_PRICE_COL_INDEX:
                return statement.ClosePrice;
            case StatementItem.QUANTITY_COL_INDEX:
                return statement.Quantity;
            case StatementItem.CURRENCY_COL_INDEX:
                return statement.Currency;
            case StatementItem.TRADE_OPENING_DATE_COL_INDEX:
                return statement.TradeOpeningDate;
            case StatementItem.OPEN_CROSS_PRICE_COL_INDEX:
                return statement.OpenCrossPrise;
            case StatementItem.POSITION_ID_COL_INDEX:
                return statement.PositionID;
            case StatementItem.TRADE_ID_COL_INDEX:
                return statement.TradeId;
            case StatementItem.DETAILS_COL_INDEX:
                return statement.Details;
            default:
                return null;
        }
    }
    GetCurrentAccount() {
        return this.statement !== null ? this.statement.Account : null;
    }
    getFormatTotalValue(column, value) {
        let formattedValue = '';
        switch (column) {
            case StatementItem.AMOUNT_COL_INDEX:
            case StatementItem.QUANTITY_COL_INDEX:
                formattedValue = value.toFixed(2);
                break;
        }
        return formattedValue;
    }
    GetColumnParams(column) {
        const result = StatementItem.columnsParams[column];
        const isHidden = result.DefaultHidden || Resources.isHidden(result.HeaderKey);
        result.Hidden = isHidden;
        result.MenuIndex = this.GetMenuIndex(result.HeaderKey);
        return result;
    }
    GetMenuIndex(headerKey) {
        if (StatementItem.ListMenuIndex.includes(headerKey)) {
            return StatementItem.ListMenuIndex.indexOf(headerKey);
        }
        else {
            return 1000;
        }
    }
    get Account() {
        var _a;
        return (_a = this.statement) === null || _a === void 0 ? void 0 : _a.Account;
    }
    getTotalValuePrecision(column, totalCurrencyAfterFiltration) {
        switch (column) {
            case StatementItem.AMOUNT_COL_INDEX:
            case StatementItem.QUANTITY_COL_INDEX:
                return 2;
            default:
                return super.getTotalValuePrecision(column, totalCurrencyAfterFiltration);
        }
    }
    GetGroupByValue(columnIndex, value, formattedValue) {
        if (value == null || value === StringUtils.NOT_AVAILABLE || value === StringUtils.NOT_AVAILABLE_OLD_CONSTANT) {
            return Resources.getResource('StatementItem.PositionID.GroupKey.EmptyValue');
        }
        switch (columnIndex) {
            case StatementItem.POSITION_ID_COL_INDEX:
                return Resources.getResource('StatementItem.PositionID.GroupKey.Format').replace('{0}', value);
            case StatementItem.OPERATION_TYPE_COL_INDEX:
                return formattedValue;
            default:
                return value;
        }
    }
}
StatementItem.ACCOUNT_COL_INDEX = 0;
StatementItem.INSTRUMENT_COL_INDEX = 1;
StatementItem.DATE_COL_INDEX = 2;
StatementItem.OPERATION_TYPE_COL_INDEX = 3;
StatementItem.OPERATION_ID_COL_INDEX = 4;
StatementItem.AMOUNT_COL_INDEX = 5;
StatementItem.OPEN_PRICE_COL_INDEX = 6;
StatementItem.CLOSE_PRICE_COL_INDEX = 7;
StatementItem.QUANTITY_COL_INDEX = 8;
StatementItem.CURRENCY_COL_INDEX = 9;
StatementItem.TRADE_OPENING_DATE_COL_INDEX = 10;
StatementItem.OPEN_CROSS_PRICE_COL_INDEX = 11;
StatementItem.POSITION_ID_COL_INDEX = 12;
StatementItem.TRADE_ID_COL_INDEX = 13;
StatementItem.DETAILS_COL_INDEX = 14;
StatementItem.columnsHeaderLocKey = {
    [StatementItem.ACCOUNT_COL_INDEX]: 'panel.statement.Account',
    [StatementItem.INSTRUMENT_COL_INDEX]: 'panel.statement.Instrument',
    [StatementItem.DATE_COL_INDEX]: 'panel.statement.Date',
    [StatementItem.OPERATION_TYPE_COL_INDEX]: 'panel.statement.Operation_type',
    [StatementItem.OPERATION_ID_COL_INDEX]: 'panel.statement.Operation_ID',
    [StatementItem.AMOUNT_COL_INDEX]: 'panel.statement.Amount',
    [StatementItem.OPEN_PRICE_COL_INDEX]: 'panel.statement.Open_price',
    [StatementItem.CLOSE_PRICE_COL_INDEX]: 'panel.statement.Close_price',
    [StatementItem.QUANTITY_COL_INDEX]: 'panel.statement.Quantity',
    [StatementItem.CURRENCY_COL_INDEX]: 'panel.statement.Currency',
    [StatementItem.TRADE_OPENING_DATE_COL_INDEX]: 'panel.statement.Trade_opening_date',
    [StatementItem.OPEN_CROSS_PRICE_COL_INDEX]: 'panel.statement.FX_rate',
    [StatementItem.POSITION_ID_COL_INDEX]: 'panel.statement.positionID',
    [StatementItem.TRADE_ID_COL_INDEX]: 'panel.statement.tradeID',
    [StatementItem.DETAILS_COL_INDEX]: 'panel.statement.details'
};
StatementItem.columnsParams = [
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.ACCOUNT_COL_INDEX], 75, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.INSTRUMENT_COL_INDEX], 75, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.DATE_COL_INDEX], 75, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.OPERATION_TYPE_COL_INDEX], 75, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.OPERATION_ID_COL_INDEX], 75, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.AMOUNT_COL_INDEX], 75, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.OPEN_PRICE_COL_INDEX], 75, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.CLOSE_PRICE_COL_INDEX], 75, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.QUANTITY_COL_INDEX], 75, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.CURRENCY_COL_INDEX], 75, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.TRADE_OPENING_DATE_COL_INDEX], 70, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.OPEN_CROSS_PRICE_COL_INDEX], 75, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.POSITION_ID_COL_INDEX], 75, QTAlignment.Right, TableTotalsVisibility.None, true, true),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.TRADE_ID_COL_INDEX], 75, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams(StatementItem.columnsHeaderLocKey[StatementItem.DETAILS_COL_INDEX], 75, QTAlignment.Left, TableTotalsVisibility.None, false, false)
];
StatementItem.ListMenuIndex = Object.values(StatementItem.columnsHeaderLocKey);
